import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { NOTES_SCROLL_PX } from '@/core/recording/session';
import { cn } from '@/lib/cn';
import { useRecordingStore } from '@/state/recording-store';

/**
 * Speaker notes that scroll by themselves while recording and hold still while
 * paused or counting down. Scrolling by hand moves the reading point; the
 * automatic scroll simply carries on from there.
 */
export function Teleprompter({ className }: { readonly className?: string }) {
  const { t } = useTranslation();
  const notes = useRecordingStore((state) => state.notes);
  const speed = useRecordingStore((state) => state.notesSpeed);
  const running = useRecordingStore((state) => state.phase.status === 'recording');
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = box.current;
    if (!element || !running) return;
    // The window this runs in may be the floating one, so its own clock drives the frames.
    const view = element.ownerDocument.defaultView ?? window;
    let frame = 0;
    let last = view.performance.now();
    // Sub-pixel steps would be rounded away by scrollTop; they are carried over instead.
    let carry = element.scrollTop;
    const tick = (now: number) => {
      const step = (NOTES_SCROLL_PX[speed] * (now - last)) / 1000;
      last = now;
      // A hand scroll since the last frame moves the reading point.
      if (Math.abs(element.scrollTop - Math.round(carry)) > 1) carry = element.scrollTop;
      carry = Math.min(carry + step, element.scrollHeight - element.clientHeight);
      element.scrollTop = carry;
      frame = view.requestAnimationFrame(tick);
    };
    frame = view.requestAnimationFrame(tick);
    return () => view.cancelAnimationFrame(frame);
  }, [running, speed]);

  return (
    <div
      ref={box}
      role="region"
      aria-label={t('record.notes')}
      tabIndex={0}
      className={cn(
        'scrollbar-thin overflow-y-auto px-4 pt-12 pb-[40%] text-[20px] leading-[1.6] font-medium tracking-[-0.01em] whitespace-pre-wrap text-fg [mask-image:linear-gradient(to_bottom,transparent,black_18%,black_82%,transparent)]',
        className,
      )}
    >
      {notes}
    </div>
  );
}
